import { Navigate, generatePath, useLocation, useParams } from 'react-router-dom'

/**
 * A `<Navigate>` whose `to` is a string drops the `?query` and the `#hash` of the URL it came from.
 * This one carries them over, for redirects whose target reads them (the filters and the view of the
 * e-mail log, the tab of an old bookmark). `pathname` is a route, absolute or relative, and never carries
 * a `?` or a `#`: the query and the hash come from the URL being left. It may have `:param` segments, which are
 * filled in with the params of the URL being left (`exchanges/templates/:id/edit` →
 * `api-import-mappings/templates/:id/edit`), so a redirect never needs the id spelled out; a `:param` the
 * route being left does not have throws.
 *
 * `setQuery` fixes parameters of the destination (the `tab` of «Ajustes»): they go first and replace an incoming
 * parameter of the same name, the rest of the incoming query follows in its order (repeated names included). Without
 * `setQuery` the incoming query goes through exactly as it came.
 */
export function RedirectKeepingLocation({
  pathname,
  setQuery,
}: {
  pathname: string
  /** Parameters set over the incoming query: they go first and replace an incoming one with the same name. */
  setQuery?: Readonly<Record<string, string>>
}) {
  const location = useLocation()
  const params = useParams()
  const search =
    setQuery === undefined ? location.search : withFixedParams(location.search, setQuery)
  return (
    <Navigate
      to={{ pathname: generatePath(pathname, params), search, hash: location.hash }}
      replace
    />
  )
}

function withFixedParams(incoming: string, fixed: Readonly<Record<string, string>>): string {
  const merged = new URLSearchParams(fixed)
  for (const [name, value] of new URLSearchParams(incoming)) {
    if (!Object.hasOwn(fixed, name)) merged.append(name, value)
  }
  const query = merged.toString()
  return query ? `?${query}` : ''
}
